import React, { useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import './chart.css';
import Blur from '../Blur/Blur';

const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

function toPersianNumber(num) {
    if (num == null) return '';

    const withCommas = Number(num).toLocaleString('en-US');
    return withCommas.replace(/\d/g, (digit) => persianDigits[digit]);
}

const persianWeekdays = {
    Saturday: 'saturday',
    Sunday: 'sunday',
    Monday: 'monday',
    Tuesday: 'tuesday',
    Wednesday: 'wednesday',
    Thursday: 'thursday',
    Friday: 'friday',
};

function Chart({ height = 320, title, weeklyReport }) {
    const { t, i18n } = useTranslation();

    // Read daily data from the new API response structure.
    const data = useMemo(() => {
        if (!weeklyReport || !Array.isArray(weeklyReport.daily)) {
            return [];
        }

        return weeklyReport.daily.map((day) => ({
            date: day.date,
            weekdayEn: day.weekday,
            nameKey: persianWeekdays[day.weekday] || day.weekday,
            value: Number(day.totalIncome) || 0,
            totalOrders: Number(day.totalOrders) || 0,
        }));
    }, [weeklyReport]);

    const formatChartNumber = useCallback(
        (value) => {
            const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';
            return new Intl.NumberFormat(locale).format(value);
        },
        [i18n.language]
    );

    const isDark = typeof document !== 'undefined' && document.body.classList.contains('dark');

    const options = useMemo(
        () => ({
            backgroundColor: 'transparent',

            grid: {
                top: 60,
                left: 20,
                right: 20,
                bottom: 40,
                containLabel: true,
            },

            title: {
                text: `${title} (${t('last_7_days')})`,
                left: 'center',
                textStyle: {
                    fontSize: 15,
                    color: isDark ? '#f5f5f5' : '#444',
                    fontFamily: 'vazir, sans-serif',
                    fontWeight: 'normal',
                },
            },

            tooltip: {
                trigger: 'axis',
                backgroundColor: isDark ? '#2a2a2a' : '#fff',
                borderColor: isDark ? '#444' : '#ddd',
                borderWidth: 1,
                textStyle: {
                    color: isDark ? '#f5f5f5' : '#333',
                },
                formatter: (params) => {
                    if (!params || params.length === 0) {
                        return '';
                    }

                    const point = params[0];
                    const day = data[point.dataIndex];

                    if (!day) {
                        return '';
                    }

                    const income = formatChartNumber(day.value);
                    const orders = formatChartNumber(day.totalOrders);

                    return [
                        point.name,
                        `${t('income')}: ${income} ${t('toman')}`,
                        `${t('total_orders')}: ${orders}`,
                    ].join('<br/>');
                },
            },

            xAxis: {
                type: 'category',
                data: data.map((day) => t(day.nameKey)),
                axisLabel: {
                    fontSize: 13,
                    show: typeof window !== 'undefined' && window.innerWidth > 549,
                    color: isDark ? '#ccc' : '#666',
                },
                boundaryGap: false,
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
            },

            yAxis: {
                type: 'value',
                axisLabel: {
                    color: isDark ? '#ccc' : '#666',
                    formatter: formatChartNumber,
                },
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                splitLine: {
                    lineStyle: {
                        color: isDark ? '#333' : '#eee',
                    },
                },
            },

            series: [
                {
                    name: t('income'),
                    type: 'line',
                    smooth: true,
                    data: data.map((day) => day.value),
                    symbol: 'none',
                    lineStyle: {
                        width: 3,
                        color: '#5e806d',
                    },
                    itemStyle: {
                        color: '#5e806d',
                    },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                {
                                    offset: 0,
                                    color: isDark
                                        ? 'rgba(83,109,239,0.45)'
                                        : 'rgba(83,109,239,0.35)',
                                },
                                {
                                    offset: 1,
                                    color: 'rgba(83,109,239,0)',
                                },
                            ],
                        },
                    },
                },
            ],
        }),
        [data, title, isDark, t, formatChartNumber]
    );

    return (
        <div className="chart-container" style={{ height }}>
            {/* <Blur text={t('upgrade_plan_blur')} /> */}
            <ReactECharts
                option={options}
                style={{ height: '100%', width: '100%' }}
                notMerge
                lazyUpdate
            />
        </div>
    );
}

export default Chart;
